import { useState } from 'react';
import { useNavigate, useRevalidator } from 'react-router';
import { FlowDefinitionSchema, TIERS, TIER_DISPLAY_NAME, type FlowStep, type FlowTrigger, type SegmentRules } from '@/types';
import { Button } from '@/components/ui/button';
import { api, errMsg } from '@/lib/api';
import { RuleBuilder } from '@/components/rule-builder';

type Template = { id: string; name: string; kind: string };
type Segment = { id: string; name: string; kind: string };

const TRIGGERS: Array<{ type: FlowTrigger['type']; label: string }> = [
  { type: 'signup', label: 'Signed up' },
  { type: 'profile_completed', label: 'Completed profile' },
  { type: 'approved', label: 'Account approved' },
  { type: 'upgrade', label: 'Upgraded plan' },
  { type: 'downgrade', label: 'Downgraded plan' },
  { type: 'cancel_requested', label: 'Requested cancellation' },
  { type: 'canceled', label: 'Subscription canceled' },
  { type: 'payment_failed', label: 'Payment failed' },
  { type: 'trial_ending', label: 'Trial ending (3 days)' },
  { type: 'segment_entered', label: 'Entered segment' },
  { type: 'segment_exited', label: 'Exited segment' },
  { type: 'tag_added', label: 'Tag added' },
  { type: 'inactive_for', label: 'Inactive for N days' },
];

function defaultStep(type: FlowStep['type'], templates: Template[]): FlowStep {
  switch (type) {
    case 'send_email':
      return { type, templateId: templates[0]?.id ?? '', domain: 'marketing' };
    case 'wait':
      return { type, duration: '1d' };
    case 'branch':
      return { type, if: { all: [{ field: 'tier', op: 'in', value: ['free'] }] }, then: [], else: [] };
    case 'add_tag':
    case 'remove_tag':
      return { type, tag: '' };
    case 'add_to_segment':
      return { type, segmentId: '' };
    case 'exit_if':
      return { type, rules: { all: [{ field: 'tier', op: 'in', value: ['free'] }] } };
    default:
      return { type: 'exit' };
  }
}

function StepList({ steps, onChange, templates, segments, depth = 0 }: { steps: FlowStep[]; onChange: (s: FlowStep[]) => void; templates: Template[]; segments: Segment[]; depth?: number }) {
  const update = (i: number, step: FlowStep) => onChange(steps.map((s, j) => (j === i ? step : s)));
  const move = (i: number, dir: -1 | 1) => {
    const next = [...steps];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const sel = 'h-9 rounded-md border px-2 text-sm bg-white';
  return (
    <div className="space-y-2">
      {steps.map((step, i) => (
        <div key={i} className="space-y-2 rounded-md border bg-white p-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">{depth === 0 ? `Step ${i + 1}` : `${i + 1}.`}</span>
            <select className={sel} value={step.type} onChange={(e) => update(i, defaultStep(e.target.value as FlowStep['type'], templates))}>
              <option value="send_email">Send email</option>
              <option value="wait">Wait</option>
              <option value="branch">Branch (if / else)</option>
              <option value="add_tag">Add tag</option>
              <option value="remove_tag">Remove tag</option>
              <option value="add_to_segment">Add to static segment</option>
              <option value="exit_if">Exit if…</option>
              <option value="exit">Exit</option>
            </select>
            {step.type === 'send_email' && (
              <>
                <select className={sel} value={step.templateId} onChange={(e) => update(i, { ...step, templateId: e.target.value })}>
                  <option value="">— template —</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
                <select className={sel} value={step.domain} onChange={(e) => update(i, { ...step, domain: e.target.value as 'marketing' | 'transactional' })}>
                  <option value="marketing">marketing domain (caps apply)</option>
                  <option value="transactional">transactional domain</option>
                </select>
                <label className="flex items-center gap-1 text-xs text-muted-foreground">
                  skip if received within
                  <input type="number" className="h-8 w-16 rounded border px-1" value={step.skipIfReceivedWithinDays ?? ''} onChange={(e) => update(i, { ...step, skipIfReceivedWithinDays: e.target.value ? Number(e.target.value) : undefined })} />
                  days
                </label>
              </>
            )}
            {step.type === 'wait' && (
              <>
                <input className="h-9 w-20 rounded-md border px-2 text-sm" value={step.duration} onChange={(e) => update(i, { ...step, duration: e.target.value })} placeholder="3d" />
                <span className="text-xs text-muted-foreground">(30m, 12h, 3d)</span>
                <label className="flex items-center gap-1 text-xs text-muted-foreground">
                  then until
                  <select className="h-8 rounded border px-1" value={step.until?.weekday ?? ''} onChange={(e) => update(i, { ...step, until: { ...(step.until ?? {}), weekday: e.target.value ? (Number(e.target.value) as 1) : undefined } })}>
                    <option value="">any weekday</option>
                    <option value="1">Mon</option>
                    <option value="2">Tue</option>
                    <option value="3">Wed</option>
                    <option value="4">Thu</option>
                    <option value="5">Fri</option>
                  </select>
                  <input type="number" min={0} max={23} className="h-8 w-14 rounded border px-1" placeholder="hour" value={step.until?.hour ?? ''} onChange={(e) => update(i, { ...step, until: e.target.value || step.until?.weekday ? { ...(step.until ?? {}), hour: e.target.value ? Number(e.target.value) : undefined, tz: 'America/New_York' } : undefined })} />
                  h ET
                </label>
              </>
            )}
            {(step.type === 'add_tag' || step.type === 'remove_tag') && <input className="h-9 w-40 rounded-md border px-2 text-sm" value={step.tag} onChange={(e) => update(i, { ...step, tag: e.target.value })} placeholder="tag" />}
            {step.type === 'add_to_segment' && (
              <select className={sel} value={step.segmentId} onChange={(e) => update(i, { ...step, segmentId: e.target.value })}>
                <option value="">— static segment —</option>
                {segments.filter((s) => s.kind === 'static').map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            )}
            <span className="ml-auto flex gap-2 text-xs">
              <button type="button" onClick={() => move(i, -1)} className="text-muted-foreground">↑</button>
              <button type="button" onClick={() => move(i, 1)} className="text-muted-foreground">↓</button>
              <button type="button" onClick={() => onChange(steps.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-red-600">remove</button>
            </span>
          </div>
          {step.type === 'exit_if' && <RuleBuilder value={step.rules as SegmentRules} onChange={(rules) => update(i, { ...step, rules })} />}
          {step.type === 'branch' && (
            <div className="space-y-2 pl-3">
              <div className="text-xs text-muted-foreground">If the contact matches:</div>
              <RuleBuilder value={step.if as SegmentRules} onChange={(rules) => update(i, { ...step, if: rules })} />
              <div className="text-xs text-muted-foreground">then:</div>
              <StepList steps={step.then} onChange={(then) => update(i, { ...step, then })} templates={templates} segments={segments} depth={depth + 1} />
              <div className="text-xs text-muted-foreground">else:</div>
              <StepList steps={step.else ?? []} onChange={(els) => update(i, { ...step, else: els })} templates={templates} segments={segments} depth={depth + 1} />
            </div>
          )}
        </div>
      ))}
      <button type="button" className="ff-link text-xs" onClick={() => onChange([...steps, defaultStep('send_email', templates)])}>
        + add step
      </button>
    </div>
  );
}

export function FlowBuilder({ templates, segments, initial }: { templates: Template[]; segments: Segment[]; initial?: { id: string; name: string; trigger: FlowTrigger; steps: FlowStep[]; reentry: string; status: string } }) {
  const navigate = useNavigate();
  const { revalidate } = useRevalidator();
  const [name, setName] = useState(initial?.name ?? '');
  const [trigger, setTrigger] = useState<FlowTrigger>(initial?.trigger ?? { type: 'signup' });
  const [steps, setSteps] = useState<FlowStep[]>(initial?.steps ?? [defaultStep('send_email', templates)]);
  const [reentry, setReentry] = useState(initial?.reentry ?? 'never');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const locked = initial?.status === 'active';

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const parsed = FlowDefinitionSchema.safeParse({ name, trigger, steps, reentry });
      if (!parsed.success) throw new Error(parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; '));
      if (initial) {
        await api(`/flows/${initial.id}`, { method: 'PATCH', json: parsed.data });
        revalidate();
      } else {
        const f = await api<{ id: string }>('/flows', { method: 'POST', json: parsed.data });
        navigate(`/flows/${f.id}`);
      }
    } catch (err) {
      setError(errMsg(err, 'Save failed'));
    } finally {
      setBusy(false);
    }
  }

  const t = trigger as Record<string, unknown>;
  const setT = (patch: Record<string, unknown>) => setTrigger({ ...trigger, ...patch } as FlowTrigger);
  const sel = 'h-9 rounded-md border px-2 text-sm bg-white';
  const tierPick = (key: 'fromTier' | 'toTier') => {
    const filter = (t.filter as Record<string, string[]> | undefined) ?? {};
    const chosen = filter[key] ?? [];
    return (
      <span className="flex flex-wrap items-center gap-1 text-xs">
        <span className="text-muted-foreground">{key === 'fromTier' ? 'from' : 'to'}</span>
        {TIERS.map((tier) => (
          <button key={tier} type="button" className={`rounded px-2 py-1 ring-1 ${chosen.includes(tier) ? 'bg-[var(--ff-primary)] text-white ring-[var(--ff-primary)]' : 'bg-white ring-border'}`} onClick={() => setT({ filter: { ...filter, [key]: chosen.includes(tier) ? chosen.filter((x) => x !== tier) : [...chosen, tier] } })}>
            {TIER_DISPLAY_NAME[tier]}
          </button>
        ))}
      </span>
    );
  };

  return (
    <div className={`space-y-4 rounded-lg border bg-white p-4 text-sm ${locked ? 'opacity-80' : ''}`}>
      {locked && <p className="rounded bg-amber-50 p-2 text-xs text-amber-900">Pause the flow to edit it.</p>}
      <fieldset disabled={locked} className="space-y-4">
        <label className="block space-y-1">
          <span className="text-xs text-muted-foreground">Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} className="h-9 w-full rounded-md border px-2" />
        </label>
        <div className="space-y-2 rounded-lg border bg-[var(--ff-surface-secondary)] p-3">
          <div className="text-xs font-medium">Trigger</div>
          <div className="flex flex-wrap items-center gap-2">
            <select className={sel} value={trigger.type} onChange={(e) => setTrigger({ type: e.target.value, ...(e.target.value === 'inactive_for' ? { days: 14 } : {}), ...(e.target.value.startsWith('segment') ? { segmentId: segments[0]?.id ?? '' } : {}), ...(e.target.value === 'tag_added' ? { tag: '' } : {}) } as FlowTrigger)}>
              {TRIGGERS.map((tr) => (
                <option key={tr.type} value={tr.type}>
                  {tr.label}
                </option>
              ))}
            </select>
            {(trigger.type === 'segment_entered' || trigger.type === 'segment_exited') && (
              <select className={sel} value={String(t.segmentId ?? '')} onChange={(e) => setT({ segmentId: e.target.value })}>
                {segments.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            )}
            {trigger.type === 'tag_added' && <input className="h-9 w-40 rounded-md border px-2 text-sm" value={String(t.tag ?? '')} onChange={(e) => setT({ tag: e.target.value })} placeholder="tag" />}
            {trigger.type === 'inactive_for' && (
              <select className={sel} value={String(t.days ?? 14)} onChange={(e) => setT({ days: Number(e.target.value) })}>
                <option value="7">7 days</option>
                <option value="14">14 days</option>
                <option value="30">30 days</option>
              </select>
            )}
          </div>
          {(trigger.type === 'upgrade' || trigger.type === 'downgrade') && (
            <div className="space-y-1">
              {tierPick('fromTier')}
              {tierPick('toTier')}
              <div className="text-xs text-muted-foreground">Leave empty to match any plan.</div>
            </div>
          )}
          <label className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">Re-entry</span>
            <select className="h-8 rounded border px-1" value={reentry} onChange={(e) => setReentry(e.target.value)}>
              <option value="never">never (once per contact)</option>
              <option value="after_exit">after the previous run finished</option>
              <option value="always">always</option>
            </select>
          </label>
        </div>
        <div className="space-y-2">
          <div className="text-xs font-medium">Steps</div>
          <StepList steps={steps} onChange={setSteps} templates={templates} segments={segments} />
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={save} disabled={busy || !name.trim()}>
            {initial ? 'Save' : 'Create draft'}
          </Button>
          {error && <span className="text-xs text-red-700">{error}</span>}
        </div>
      </fieldset>
    </div>
  );
}
